import * as React from "react";
import { cva } from "class-variance-authority";
import { ArrowDownRight, ArrowUpRight, ChevronRight, Minus } from "./icons";

import { cn } from "utils/cn";
import { Skeleton } from "./Skeleton";

export type StatTone = "neutral" | "primary" | "success" | "warning" | "danger";

const iconChipVariants = cva(
  "flex h-9 w-9 shrink-0 items-center justify-center rounded-control [&_svg]:h-[18px] [&_svg]:w-[18px]",
  {
    variants: {
      tone: {
        neutral: "bg-surface-muted text-muted-foreground",
        primary: "bg-primary-subtle text-primary-text",
        success: "bg-success-subtle text-success-text",
        warning: "bg-warning-subtle text-warning-text",
        danger: "bg-danger-subtle text-danger-text",
      },
    },
    defaultVariants: { tone: "neutral" },
  }
);

const trendVariants = cva(
  "inline-flex items-center gap-0.5 rounded-md px-1.5 py-0.5 text-xs font-medium tabular-nums [&_svg]:h-3.5 [&_svg]:w-3.5",
  {
    variants: {
      outcome: {
        good: "bg-success-subtle text-success-text",
        bad: "bg-danger-subtle text-danger-text",
        neutral: "bg-surface-muted text-muted-foreground",
      },
    },
    defaultVariants: { outcome: "neutral" },
  }
);

export interface StatTrend {
  /** Already formatted, e.g. "+12%" or "3 more". */
  value: string;
  direction: "up" | "down" | "flat";
  /**
   * Whether this change is good news. Defaults to up = good, down = bad; pass `false` for metrics
   * where going up is bad (overdue amount, failed syncs).
   */
  positive?: boolean;
}

export interface StatCardProps extends Omit<React.HTMLAttributes<HTMLElement>, "title"> {
  label: React.ReactNode;
  value: React.ReactNode;
  /** Short context under the value, e.g. "across 42 clients". */
  hint?: React.ReactNode;
  icon?: React.ReactNode;
  /** Tints the icon chip only; the value always stays in the foreground color. */
  tone?: StatTone;
  trend?: StatTrend;
  loading?: boolean;
  /**
   * Makes the whole tile a button (hover lift, focus ring, a chevron), e.g. to open a breakdown.
   * Pass `aria-haspopup="dialog"` when it opens a drawer or dialog.
   */
  onClick?: React.MouseEventHandler<HTMLElement>;
}

const trendIcon: Record<StatTrend["direction"], React.ReactNode> = {
  up: <ArrowUpRight aria-hidden="true" />,
  down: <ArrowDownRight aria-hidden="true" />,
  flat: <Minus aria-hidden="true" />,
};

const trendSrText: Record<StatTrend["direction"], string> = {
  up: "Up",
  down: "Down",
  flat: "No change",
};

const trendOutcome = ({ direction, positive }: StatTrend): "good" | "bad" | "neutral" => {
  if (direction === "flat") {
    return "neutral";
  }
  const good = positive ?? direction === "up";
  return good ? "good" : "bad";
};

const cardClasses =
  "rounded-card border border-border bg-surface px-5 py-4 text-foreground shadow-card";

// A clickable tile: the whole card is the button, so its name reads label, value and hint. A
// flex column keeps the content at the top (buttons centre it vertically by default).
const clickableClasses = cn(
  "group relative flex w-full flex-col text-left transition-[border-color,box-shadow,transform] duration-base",
  "hover:-translate-y-px hover:border-primary/40 hover:shadow-popover",
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
  "motion-reduce:transition-none motion-reduce:hover:translate-y-0"
);

/**
 * KPI tile: label, a large value, and an optional trend and hint underneath. With `onClick` the
 * tile is a button. The inner parts are spans so the markup stays valid inside a button.
 */
export const StatCard = React.forwardRef<HTMLElement, StatCardProps>(
  (
    {
      label,
      value,
      hint,
      icon,
      tone = "neutral",
      trend,
      loading = false,
      onClick,
      className,
      ...props
    },
    ref
  ) => {
    const body = (
      <>
        <span className="flex items-start justify-between gap-3">
          <span className="block min-w-0 pt-0.5 text-sm font-medium text-muted-foreground">
            {label}
          </span>
          {icon && (
            <span aria-hidden="true" className={iconChipVariants({ tone })}>
              {icon}
            </span>
          )}
        </span>
        {loading ? (
          <>
            <Skeleton className={cn("block h-8 w-28", icon ? "mt-1" : "mt-2")} />
            {(hint || trend) && <Skeleton className="mt-2 block h-4 w-36" />}
          </>
        ) : (
          <>
            <span
              className={cn(
                "block truncate text-2xl font-semibold tracking-tight tabular-nums",
                icon ? "mt-1" : "mt-2"
              )}
            >
              {value}
            </span>
            {(hint || trend) && (
              <span
                className={cn(
                  "mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground",
                  onClick && "pr-5"
                )}
              >
                {trend && (
                  <span className={trendVariants({ outcome: trendOutcome(trend) })}>
                    {trendIcon[trend.direction]}
                    <span className="sr-only">{trendSrText[trend.direction]}</span>
                    {trend.value}
                  </span>
                )}
                {hint && <span className="min-w-0">{hint}</span>}
              </span>
            )}
          </>
        )}
        {onClick && (
          <ChevronRight
            aria-hidden="true"
            className="absolute bottom-4 right-4 h-4 w-4 text-subtle-foreground transition-[color,transform] duration-base group-hover:translate-x-0.5 group-hover:text-primary-text motion-reduce:transition-none"
          />
        )}
      </>
    );

    if (onClick) {
      return (
        <button
          ref={ref as React.Ref<HTMLButtonElement>}
          type="button"
          data-ui="stat-card"
          aria-busy={loading || undefined}
          onClick={onClick}
          className={cn(cardClasses, clickableClasses, className)}
          {...props}
        >
          {body}
        </button>
      );
    }
    return (
      <div
        ref={ref as React.Ref<HTMLDivElement>}
        data-ui="stat-card"
        aria-busy={loading || undefined}
        className={cn(cardClasses, className)}
        {...props}
      >
        {body}
      </div>
    );
  }
);
StatCard.displayName = "StatCard";
